<!--
  ChatTab.vue - Компонент чату для тікетів

  Функціонал:
  - Перегляд всіх повідомлень з красивим сучасним дизайном
  - Відповідь на повідомлення (кнопка + подвійний клік)
  - Видалення повідомлень (тільки свої)
  - Редагування повідомлень (тільки свої)
  - Відправка повідомлень з автоматичним розширенням textarea
  - Аватари користувачів з унікальними кольорами
  - Плавні анімації та переходи
  - Fullscreen режим
  - Автоматичний скрол до нових повідомлень
  - Підсвітка повідомлення при переході до нього
  - Responsive дизайн

  Тестові дані: У data() є масив messages з тестовими повідомленнями.
  Для підключення API: Закоментуйте тестові дані і розкоментуйте loadMessages() у mounted()
-->

<template>
  <div class="chat-tab">
    <!-- Хедер чату з кнопками -->
    <div class="chat-header">
      <div class="chat-header-title">
        <i class="mdi mdi-chat"></i>
        <span>Чат по тікету</span>
      </div>
      <div class="chat-header-actions">
        <button
          class="chat-btn chat-btn-icon"
          @click="loadMessages"
          :disabled="loading"
          title="Оновити"
        >
          <i class="mdi mdi-refresh" :class="{ rotating: loading }"></i>
        </button>
        <button
          class="chat-btn chat-btn-icon"
          @click="toggleFullscreen"
          title="Розгорнути на весь екран"
        >
          <i
            class="mdi"
            :class="isFullscreen ? 'mdi-fullscreen-exit' : 'mdi-fullscreen'"
          ></i>
        </button>
      </div>
    </div>

    <!-- Контейнер повідомлень (скроллиться) -->
    <div class="chat-container" ref="chatContainer">
      <div v-if="loading && messages.length === 0" class="chat-state-screen">
        <div class="loading-state-animation">
          <div class="spinner"></div>
        </div>
        <p>Завантаження повідомлень...</p>
        <span>Будь ласка, зачекайте</span>
      </div>

      <div v-else-if="messages.length === 0" class="chat-state-screen">
        <div class="empty-state-animation">
          <i class="mdi mdi-chat-outline"></i>
          <div class="empty-state-dots">
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>
        <p>Немає повідомлень</p>
        <span>Почніть розмову, надіславши перше повідомлення</span>
        <div class="empty-state-hint">
          <i class="mdi mdi-information-outline"></i>
          <span>Ви можете написати повідомлення в полі внизу</span>
        </div>
      </div>

      <div v-else class="chat-messages">
        <!-- Індикатор набору тексту (для демонстрації, можна буде підключити до API) -->
        <!-- <div class="typing-indicator" v-if="someoneTyping">
          <div class="typing-indicator-content">
            <div class="typing-dot"></div>
            <div class="typing-dot"></div>
            <div class="typing-dot"></div>
          </div>
        </div> -->

        <div
          v-for="message in sortedMessages"
          :key="message.id"
          class="message-wrapper"
          :class="{ 'message-wrapper-own': isOwnMessage(message.workerId) }"
        >
          <!-- Повідомлення на яке відповідають (якщо є) -->
          <div
            v-if="message.replyTo"
            class="reply-preview"
            @click="scrollToMessage(message.replyMessageId)"
          >
            <div class="reply-preview-line"></div>
            <div class="reply-preview-content">
              <div class="reply-preview-author">
                {{ getWorkerName(message.replyTo.workerId) }}
              </div>
              <div
                class="reply-preview-text"
                v-html="truncateText(message.replyTo.message)"
              ></div>
            </div>
          </div>

          <!-- Саме повідомлення -->
          <div
            class="message"
            :class="{ 'message-own': isOwnMessage(message.workerId) }"
            :data-message-id="message.id"
            @dblclick="startReply(message)"
            :title="'Подвійний клік для відповіді'"
          >
            <div class="message-avatar" v-if="!isOwnMessage(message.workerId)">
              <div class="avatar" :style="getAvatarStyle(message.workerId)">
                <img
                  v-if="getWorkerPhoto(message.workerId)"
                  :src="getWorkerPhoto(message.workerId)"
                  :alt="getWorkerName(message.workerId)"
                  class="avatar-image"
                />
                <span v-else class="avatar-initials">
                  {{ getWorkerInitials(message.workerId) }}
                </span>
              </div>
            </div>
            <div class="message-content">
              <div class="message-header">
                <span class="message-author">{{
                  getWorkerName(message.workerId)
                }}</span>
                <span class="message-time">{{
                  formatTime(message.datetime)
                }}</span>
              </div>
              <div class="message-text" v-html="message.message"></div>
              <div class="message-actions">
                <button
                  class="message-action-btn"
                  @click="startReply(message)"
                  title="Відповісти"
                >
                  <i class="mdi mdi-reply"></i>
                </button>
                <button
                  v-if="isOwnMessage(message.workerId)"
                  class="message-action-btn"
                  @click="startEdit(message)"
                  title="Редагувати"
                >
                  <i class="mdi mdi-pencil"></i>
                </button>
                <button
                  v-if="isOwnMessage(message.workerId)"
                  class="message-action-btn message-action-delete"
                  @click="deleteMessage(message.id)"
                  title="Видалити"
                >
                  <i class="mdi mdi-delete"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Поле вводу -->
    <div class="chat-input-container">
      <!-- Попередній перегляд відповіді/редагування -->
      <div v-if="replyingTo || editingMessage" class="chat-reply-preview">
        <div class="reply-preview-line"></div>
        <div class="reply-preview-content">
          <div class="reply-preview-header">
            <span>{{ editingMessage ? "Редагування" : "Відповідь на" }}</span>
            <button class="reply-preview-close" @click="cancelReply">
              <i class="mdi mdi-close"></i>
            </button>
          </div>
          <div class="reply-preview-author">
            {{
              editingMessage
                ? "Ваше повідомлення"
                : getWorkerName(replyingTo?.workerId)
            }}
          </div>
          <div
            class="reply-preview-text"
            v-html="
              truncateText(editingMessage?.message || replyingTo?.message)
            "
          ></div>
        </div>
      </div>

      <!-- Textarea для вводу -->
      <div class="chat-input-wrapper">
        <div class="chat-input-container-inner">
          <textarea
            ref="messageInput"
            v-model="newMessage"
            @input="autoResizeTextarea"
            @keydown.enter.exact.prevent="sendMessage"
            @keydown.enter.shift.exact="newMessage += '\n'"
            placeholder="Напишіть повідомлення..."
            class="chat-input"
            rows="1"
          ></textarea>
          <div class="chat-input-hint">
            <span><kbd>Enter</kbd> відправити</span>
            <span><kbd>Shift+Enter</kbd> новий рядок</span>
            <span><kbd>Подвійний клік</kbd> на повідомлення - відповідь</span>
          </div>
        </div>
        <button
          class="chat-send-btn"
          @click="sendMessage"
          :disabled="!newMessage.trim() || sending"
        >
          <i class="mdi mdi-send" v-if="!sending"></i>
          <div class="spinner-small" v-else></div>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { Tickets } from "@/API.js";
import { storeS } from "@/store";

const api = new Tickets();

export default {
  name: "ChatTab",
  props: {
    ticket: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      messages: [],
      workers: {}, // Кеш інформації про працівників
      // ===== ТЕСТОВІ ДАНІ =====
      //
      // Режим 1: Порожній чат (для перевірки empty state)
      // messages: []
      //
      // Режим 2: Чат з повідомленнями (розкоментуйте нижче)
      // [
      // {
      //   id: 1,
      //   workerId: 101,
      //   message: "Привіт! У мене виникла проблема з авторизацією в системі.",
      //   datetime: new Date(Date.now() - 7200000).toISOString(),
      //   deleted: false,
      // },
      // {
      //   id: 2,
      //   workerId: currentUserId,
      //   message: 'Доброго дня! Будь ласка, опишіть більш детально, яка саме помилка виникає?',
      //   datetime: new Date(Date.now() - 7000000).toISOString(),
      //   deleted: false
      // },
      // {
      //   id: 3,
      //   workerId: 101,
      //   message: 'При вході з\'являється повідомлення "Invalid credentials". Я впевнений, що пароль правильний.',
      //   datetime: new Date(Date.now() - 6800000).toISOString(),
      //   deleted: false,
      //   replyMessageId: 2
      // },
      // {
      //   id: 4,
      //   workerId: currentUserId,
      //   message: 'Зрозуміло. Спробуйте, будь ласка, скинути пароль через форму відновлення. Також перевірте, чи не увімкнено Caps Lock.',
      //   datetime: new Date(Date.now() - 6600000).toISOString(),
      //   deleted: false,
      //   replyMessageId: 3
      // },
      // {
      //   id: 5,
      //   workerId: 101,
      //   message: 'Дякую, спробую! Також помітив, що кнопка "Забули пароль?" не працює на мобільній версії.',
      //   datetime: new Date(Date.now() - 6400000).toISOString(),
      //   deleted: false
      // },
      // {
      //   id: 6,
      //   workerId: currentUserId,
      //   message: 'Дякую за інформацію! Я передам цю проблему в технічний відділ. Вони виправлять це найближчим часом.',
      //   datetime: new Date(Date.now() - 6200000).toISOString(),
      //   deleted: false,
      //   replyMessageId: 5
      // },
      // {
      //   id: 7,
      //   workerId: 102,
      //   message: 'Я також підтверджую цю проблему на iOS Safari. Кнопка не реагує на кліки.',
      //   datetime: new Date(Date.now() - 6000000).toISOString(),
      //   deleted: false
      // },
      // {
      //   id: 8,
      //   workerId: currentUserId,
      //   message: 'Дякую за підтвердження! Додам цю інформацію до звіту.',
      //   datetime: new Date(Date.now() - 5800000).toISOString(),
      //   deleted: false,
      //   replyMessageId: 7
      // }
      // ]
      //
      // Режим 3: Підключення до API
      // Замініть messages: [] і розкоментуйте loadMessages() у mounted()
      newMessage: "",
      loading: false,
      sending: false,
      replyingTo: null,
      editingMessage: null,
      isFullscreen: false,
    };
  },
  computed: {
    sortedMessages() {
      // Сортуємо повідомлення за датою (старі зверху)
      const sorted = [...this.messages].sort((a, b) => {
        return new Date(a.datetime) - new Date(b.datetime);
      });

      // Додаємо інформацію про повідомлення на яке відповідають
      return sorted.map((msg) => {
        if (msg.replyMessageId) {
          const replyTo = this.messages.find(
            (m) => m.id === msg.replyMessageId
          );
          return { ...msg, replyTo };
        }
        return msg;
      });
    },
    currentWorkerId() {
      return storeS?.userbase?.userid || null;
    },
    user() {
      return storeS.userbase;
    },
  },
  mounted() {
    // Для тестових даних закоментовано
    // Розкоментуйте коли підключите API:
    this.loadMessages();
    // Автоматичне оновлення кожні 30 секунд
    // this.autoRefreshInterval = setInterval(() => {
    //   this.loadMessages(true);
    // }, 30000);

    // Скролимо до низу для показу всіх тестових повідомлень
    this.$nextTick(() => {
      this.scrollToBottom();
    });
  },
  beforeUnmount() {
    if (this.autoRefreshInterval) {
      clearInterval(this.autoRefreshInterval);
    }
  },
  methods: {
    async loadMessages(silent = false) {
      if (!silent) this.loading = true;
      try {
        const response = await api.getMessages(this.ticket.ticketId);
        if (response.status === "done" && response.data) {
          this.messages = response.data.filter((msg) => !msg.deleted);

          // Завантажуємо інформацію про працівників
          await this.loadWorkersInfo();

          if (!silent) {
            this.$nextTick(() => {
              this.scrollToBottom();
            });
          }
        }
      } catch (error) {
        console.error("Помилка завантаження повідомлень:", error);
        this.$toast.error("Не вдалося завантажити повідомлення");
      } finally {
        this.loading = false;
      }
    },

    async loadWorkersInfo() {
      // Отримуємо унікальні ID працівників з повідомлень
      const workerIds = [...new Set(this.messages.map(msg => msg.workerId))];

      // Завантажуємо дані для кожного працівника
      for (const workerId of workerIds) {
        if (!this.workers[workerId]) {
          try {
            const response = await api.getWorker(workerId);
            if (response.status === "done" && response.data) {
              this.$set(this.workers, workerId, response.data);
            }
          } catch (error) {
            console.error(`Помилка завантаження працівника ${workerId}:`, error);
          }
        }
      }
    },

    async sendMessage() {
      if (!this.newMessage.trim() || this.sending) return;

      this.sending = true;
      try {
        const messageData = {
          message: this.newMessage.trim(),
        };

        if (this.editingMessage) {
          // Редагування повідомлення
          await api.editMessage(this.editingMessage.id, messageData);
          this.$toast.success("Повідомлення відредаговано");
        } else if (this.replyingTo) {
          // Відповідь на повідомлення
          await api.replyOnMessage(
            this.ticket.ticketId,
            this.replyingTo.id,
            messageData
          );
          this.$toast.success("Відповідь надіслано");
        } else {
          // Нове повідомлення
          await api.sendMessage(this.ticket.ticketId, messageData);
          this.$toast.success("Повідомлення надіслано");
        }

        this.newMessage = "";
        this.cancelReply();
        // Відновлюємо розмір textarea
        this.$nextTick(() => {
          const textarea = this.$refs.messageInput;
          if (textarea) {
            textarea.style.height = "auto";
          }
        });
        await this.loadMessages();
      } catch (error) {
        console.error("Помилка відправки повідомлення:", error);
        this.$toast.error("Не вдалося надіслати повідомлення");
      } finally {
        this.sending = false;
      }
    },

    async deleteMessage(messageId) {
      if (!confirm("Ви впевнені, що хочете видалити це повідомлення?")) return;

      try {
        await api.deleteMessage(messageId);
        this.$toast.success("Повідомлення видалено");
        await this.loadMessages();
      } catch (error) {
        console.error("Помилка видалення повідомлення:", error);
        this.$toast.error("Не вдалося видалити повідомлення");
      }
    },

    startReply(message) {
      this.replyingTo = message;
      this.editingMessage = null;
      this.$refs.messageInput.focus();
    },

    startEdit(message) {
      this.editingMessage = message;
      this.replyingTo = null;
      this.newMessage = message.message.replace(/<[^>]*>/g, ""); // Видаляємо HTML теги
      this.$refs.messageInput.focus();
    },

    cancelReply() {
      const wasEditing = this.editingMessage !== null;
      this.replyingTo = null;
      this.editingMessage = null;
      if (wasEditing) {
        this.newMessage = "";
      }
    },

    scrollToBottom() {
      const container = this.$refs.chatContainer;
      if (container) {
        container.scrollTo({
          top: container.scrollHeight,
          behavior: "smooth",
        });
      }
    },

    scrollToMessage(messageId) {
      const messageEl = document.querySelector(
        `[data-message-id="${messageId}"]`
      );
      if (messageEl) {
        messageEl.scrollIntoView({ behavior: "smooth", block: "center" });
        messageEl.classList.add("message-highlight");
        setTimeout(() => {
          messageEl.classList.remove("message-highlight");
        }, 2000);
      }
    },

    toggleFullscreen() {
      this.isFullscreen = !this.isFullscreen;
      const chatTab = this.$el;
      if (this.isFullscreen) {
        chatTab.classList.add("chat-fullscreen");
      } else {
        chatTab.classList.remove("chat-fullscreen");
      }
    },

    isOwnMessage(workerId) {
      return workerId === this.currentWorkerId;
    },

    getWorkerName(workerId) {
      if (this.isOwnMessage(workerId)) {
        const user = this.user;
        return user?.name || "Ви";
      }

      const worker = this.workers[workerId];
      if (worker && worker.name) {
        return worker.name;
      }

      return `Користувач #${workerId}`;
    },

    getWorkerInitials(workerId) {
      const name = this.getWorkerName(workerId);

      // Якщо це просто "Ви", повертаємо перші дві літери імені користувача
      if (name === "Ви" && this.user?.name) {
        const fullName = this.user.name;
        const words = fullName.split(" ");
        if (words.length >= 2) {
          return (words[0][0] + words[1][0]).toUpperCase();
        }
        return fullName.substring(0, 2).toUpperCase();
      }

      // Для інших користувачів
      const words = name.split(" ");
      if (words.length >= 2) {
        return (words[0][0] + words[1][0]).toUpperCase();
      }
      return name.substring(0, 2).toUpperCase();
    },

    getWorkerPhoto(workerId) {
      const worker = this.workers[workerId];
      return worker?.photo || null;
    },

    getAvatarStyle(workerId) {
      // Різні градієнти для різних користувачів
      const gradients = [
        "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
        "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
        "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
        "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
        "linear-gradient(135deg, #30cfd0 0%, #330867 100%)",
        "linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)",
        "linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)",
      ];
      const index = workerId % gradients.length;
      return { background: gradients[index] };
    },

    formatTime(datetime) {
      const date = new Date(datetime);
      const now = new Date();
      const isToday = date.toDateString() === now.toDateString();

      if (isToday) {
        return date.toLocaleTimeString("uk-UA", {
          hour: "2-digit",
          minute: "2-digit",
        });
      } else {
        return date.toLocaleString("uk-UA", {
          day: "2-digit",
          month: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
        });
      }
    },

    truncateText(text, maxLength = 50) {
      if (!text) return "";
      const plainText = text.replace(/<[^>]*>/g, "");
      return plainText.length > maxLength
        ? plainText.substring(0, maxLength) + "..."
        : plainText;
    },

    autoResizeTextarea() {
      const textarea = this.$refs.messageInput;
      if (textarea) {
        textarea.style.height = "auto";
        textarea.style.height = Math.min(textarea.scrollHeight, 150) + "px";
      }
    },
  },
};
</script>

<style scoped>
.chat-tab {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: linear-gradient(135deg, #f5f7fa 0%, #ffffff 100%);
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  /* overflow: hidden;
  min-height: 0; */
}

.chat-tab.chat-fullscreen {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9999;
  border-radius: 0;
  animation: fullscreenOpen 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

@keyframes fullscreenOpen {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

/* Хедер чату */
.chat-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);
}

.chat-header-title {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 17px;
  font-weight: 600;
  color: #1a202c;
}

.chat-header-title i {
  font-size: 24px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.chat-header-actions {
  display: flex;
  gap: 8px;
}

.chat-btn {
  padding: 8px 16px;
  border: none;
  border-radius: 10px;
  background: #f7fafc;
  color: #4a5568;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 6px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.chat-btn-icon {
  padding: 10px 12px;
}

.chat-btn:hover:not(:disabled) {
  background: #edf2f7;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.chat-btn:active:not(:disabled) {
  transform: translateY(0);
}

.chat-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.chat-btn i {
  font-size: 18px;
}

/* Контейнер повідомлень */
.chat-container {
  flex: 1 1 auto;
  overflow-y: auto;
  overflow-x: hidden;
  background: linear-gradient(to bottom, #f7fafc 0%, #edf2f7 100%);
  min-height: 0;
  scroll-behavior: smooth;
}

/* Екрани станів (завантаження, немає повідомлень) */
.chat-state-screen {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100%;
  padding: 40px 16px;
  color: #a0aec0;
  text-align: center;
  gap: 16px;
}

.loading-state-animation {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  animation: floatAnimation 3s ease-in-out infinite;
}

.empty-state-animation {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  animation: floatAnimation 3s ease-in-out infinite;
}

@keyframes floatAnimation {
  0%,
  100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
}

.chat-state-screen i {
  font-size: 80px;
  margin-bottom: 10px;
  opacity: 0.4;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: iconPulse 2s ease-in-out infinite;
}

@keyframes iconPulse {
  0%,
  100% {
    opacity: 0.4;
    transform: scale(1);
  }
  50% {
    opacity: 0.6;
    transform: scale(1.05);
  }
}

.empty-state-dots {
  display: flex;
  gap: 6px;
  margin-top: 8px;
}

.empty-state-dots span {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  animation: dotBounce 1.4s infinite ease-in-out;
}

.empty-state-dots span:nth-child(1) {
  animation-delay: 0s;
}

.empty-state-dots span:nth-child(2) {
  animation-delay: 0.2s;
}

.empty-state-dots span:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes dotBounce {
  0%,
  60%,
  100% {
    transform: translateY(0);
    opacity: 0.4;
  }
  30% {
    transform: translateY(-10px);
    opacity: 1;
  }
}

.chat-state-screen p {
  margin: 0;
  font-size: 19px;
  font-weight: 600;
  color: #4a5568;
}

.chat-state-screen > span {
  font-size: 14px;
  color: #a0aec0;
  font-weight: 400;
  max-width: 300px;
  line-height: 1.5;
}

.empty-state-hint {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  background: rgba(102, 126, 234, 0.08);
  border-radius: 12px;
  margin-top: 16px;
  border: 1px solid rgba(102, 126, 234, 0.15);
  animation: hintFadeIn 0.5s ease-out 0.3s both;
}

@keyframes hintFadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.empty-state-hint i {
  font-size: 18px;
  margin: 0;
  opacity: 0.6;
  animation: none;
}

.empty-state-hint span {
  font-size: 13px;
  color: #718096;
  font-weight: 500;
}

/* Повідомлення */
.chat-messages {
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 16px;
  min-height: 100%;
}

.message-wrapper {
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: flex-start;
}

.message-wrapper-own {
  align-items: flex-end;
}

.message {
  display: flex;
  gap: 12px;
  max-width: 65%;
  animation: messageAppear 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  user-select: none;
  position: relative;
  align-items: flex-end;
}

.message::before {
  content: "";
  position: absolute;
  top: 50%;
  left: -12px;
  transform: translateY(-50%);
  width: 4px;
  height: 0;
  background: linear-gradient(to bottom, #667eea, #764ba2);
  border-radius: 2px;
  transition: height 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  opacity: 0;
}

.message:hover::before {
  height: 60%;
  opacity: 1;
}

.message-avatar {
  flex-shrink: 0;
  margin-bottom: 4px;
}

.avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 600;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
  position: relative;
}

.avatar:hover {
  transform: scale(1.1);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
}

.avatar-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  position: absolute;
  top: 0;
  left: 0;
}

.avatar-initials {
  position: relative;
  z-index: 1;
}

@keyframes messageAppear {
  from {
    opacity: 0;
    transform: translateY(15px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.message-own {
  align-self: flex-end;
  flex-direction: row-reverse;
}

.message-own::before {
  left: auto;
  right: -12px;
}

.message-content {
  background: #ffffff;
  border-radius: 16px 16px 16px 4px;
  padding: 12px 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06), 0 1px 3px rgba(0, 0, 0, 0.04);
  position: relative;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid rgba(0, 0, 0, 0.05);
}

.message-own .message-content {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #ffffff;
  border-radius: 16px 16px 4px 16px;
  border: none;
  box-shadow: 0 3px 12px rgba(102, 126, 234, 0.25),
    0 1px 4px rgba(102, 126, 234, 0.15);
}

.message-content:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1), 0 2px 6px rgba(0, 0, 0, 0.06);
  transform: translateY(-2px);
}

.message-own .message-content:hover {
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4),
    0 3px 8px rgba(102, 126, 234, 0.25);
}

.message-content:hover .message-actions {
  opacity: 1;
  transform: translateY(0);
}

.message-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 6px;
}

.message-author {
  font-size: 12px;
  font-weight: 600;
  color: #4a5568;
  letter-spacing: 0.01em;
}

.message-own .message-author {
  color: rgba(255, 255, 255, 0.9);
}

.message-time {
  font-size: 11px;
  color: #a0aec0;
  font-weight: 400;
}

.message-own .message-time {
  color: rgba(255, 255, 255, 0.7);
}

.message-text {
  font-size: 14px;
  line-height: 1.5;
  color: #2d3748;
  word-wrap: break-word;
  font-weight: 400;
}

.message-own .message-text {
  color: #ffffff;
  font-weight: 400;
}

.message-actions {
  display: flex;
  gap: 6px;
  margin-top: 10px;
  opacity: 0;
  transform: translateY(-5px);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.message-action-btn {
  padding: 6px 10px;
  border: none;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.04);
  color: #4a5568;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.message-own .message-action-btn {
  background: rgba(255, 255, 255, 0.15);
  color: #ffffff;
}

.message-action-btn:hover {
  background: rgba(0, 0, 0, 0.08);
  transform: scale(1.15);
}

.message-own .message-action-btn:hover {
  background: rgba(255, 255, 255, 0.25);
}

.message-action-delete:hover {
  background: #fc8181 !important;
  color: #ffffff !important;
  box-shadow: 0 2px 8px rgba(252, 129, 129, 0.4);
}

.message-highlight {
  animation: highlight 2s cubic-bezier(0.4, 0, 0.2, 1);
}

@keyframes highlight {
  0% {
    transform: scale(1);
  }
  10% {
    transform: scale(1.03);
  }
  20%,
  80% {
    transform: scale(1);
    box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.3);
  }
  100% {
    transform: scale(1);
    box-shadow: none;
  }
}

/* Попередній перегляд відповіді */
.reply-preview {
  margin-left: 52px;
  padding: 10px 14px;
  background: rgba(102, 126, 234, 0.08);
  border-left: 3px solid #667eea;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  gap: 10px;
  backdrop-filter: blur(5px);
}

.message-own + .message-wrapper .reply-preview {
  margin-left: 0;
  margin-right: 0;
}

.reply-preview:hover {
  background: rgba(102, 126, 234, 0.12);
  transform: translateX(4px);
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.15);
}

.reply-preview-line {
  width: 3px;
  background: linear-gradient(to bottom, #667eea 0%, #764ba2 100%);
  border-radius: 2px;
  flex-shrink: 0;
}

.reply-preview-content {
  flex: 1;
  min-width: 0;
}

.reply-preview-author {
  font-size: 12px;
  font-weight: 600;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 3px;
}

.reply-preview-text {
  font-size: 13px;
  color: #718096;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 500;
}

/* Поле вводу (фіксований внизу) */
.chat-input-container {
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(10px);
  padding: 16px;
  /* flex-shrink: 0;
  flex-grow: 0; */
  flex: 0 0 auto;
  box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.03);
  /* position: sticky;
  bottom: 0; */
  z-index: 10;
  /* margin-top: auto; */
}

.chat-reply-preview {
  display: flex;
  gap: 10px;
  padding: 14px 16px;
  background: linear-gradient(
    135deg,
    rgba(102, 126, 234, 0.08) 0%,
    rgba(118, 75, 162, 0.08) 100%
  );
  border-radius: 12px;
  margin-bottom: 14px;
  border: 1px solid rgba(102, 126, 234, 0.15);
  backdrop-filter: blur(5px);
}

.reply-preview-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
  font-size: 12px;
  font-weight: 600;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.reply-preview-close {
  padding: 4px 8px;
  border: none;
  background: rgba(0, 0, 0, 0.04);
  color: #718096;
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
}

.reply-preview-close:hover {
  background: rgba(0, 0, 0, 0.08);
  color: #4a5568;
  transform: scale(1.1);
}

.chat-input-wrapper {
  display: flex;
  gap: 8px;
  /* align-items: center; */
}

.chat-input-container-inner {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.chat-input {
  width: 100%;
  padding: 10px 14px;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  font-size: 14px;
  font-family: inherit;
  resize: none;
  min-height: 44px;
  max-height: 150px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: #ffffff;
  color: #2d3748;
  line-height: 1.5;
}

.chat-input:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  background: #ffffff;
}

.chat-input::placeholder {
  color: #a0aec0;
}

.chat-input-hint {
  display: flex;
  gap: 12px;
  font-size: 11px;
  color: #a0aec0;
  padding: 0 4px;
  opacity: 0;
  transform: translateY(-5px);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.chat-input:focus + .chat-input-hint {
  opacity: 1;
  transform: translateY(0);
}

.chat-input-hint kbd {
  padding: 2px 6px;
  border-radius: 4px;
  background: #edf2f7;
  color: #4a5568;
  font-size: 10px;
  font-weight: 600;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.chat-send-btn {
  padding: 10px 14px;
  border: none;
  border-radius: 10px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #ffffff;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 44px;
  height: 44px;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.2);
}

.chat-send-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.chat-send-btn:active:not(:disabled) {
  transform: translateY(0);
}

.chat-send-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.chat-send-btn i {
  font-size: 18px;
}

/* Анімації завантаження */
.spinner {
  width: 48px;
  height: 48px;
  border: 4px solid rgba(102, 126, 234, 0.1);
  border-top: 4px solid #667eea;
  border-radius: 50%;
  animation: spin 0.9s cubic-bezier(0.4, 0, 0.2, 1) infinite;
  margin-bottom: 20px;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.15);
}

.spinner-small {
  width: 20px;
  height: 20px;
  border: 3px solid rgba(255, 255, 255, 0.25);
  border-top: 3px solid #ffffff;
  border-radius: 50%;
  animation: spin 0.7s cubic-bezier(0.4, 0, 0.2, 1) infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.rotating {
  animation: spin 1s cubic-bezier(0.4, 0, 0.2, 1) infinite;
}

/* Скролбар */

/* Typing Indicator */
.typing-indicator {
  display: flex;
  max-width: 65%;
  margin-bottom: 20px;
}

.typing-indicator-content {
  background: #ffffff;
  border-radius: 16px 16px 16px 4px;
  padding: 16px 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  display: flex;
  gap: 6px;
  align-items: center;
}

.typing-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #a0aec0;
  animation: typingAnimation 1.4s infinite;
}

.typing-dot:nth-child(2) {
  animation-delay: 0.2s;
}

.typing-dot:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes typingAnimation {
  0%,
  60%,
  100% {
    opacity: 0.3;
    transform: scale(0.8);
  }
  30% {
    opacity: 1;
    transform: scale(1.2);
  }
}

/* Responsive */
@media (max-width: 768px) {
  .chat-messages {
    padding: 16px;
  }

  .message {
    max-width: 85%;
  }

  .chat-input-container {
    padding: 16px;
  }

  .chat-header {
    padding: 16px;
  }

  .chat-input-hint {
    display: none;
  }
}
</style>
